import { useIsJobillan, useJobillaOrgId } from '@/hooks'
import {
	organisationRelationShowQueryKey,
	type OrganisationRelationShowResponse,
	useOrganisationRelationShow,
	useOrganisationUpdate,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { CareerSiteSettings, type CareerSiteViewModel, SettingsHeading, SettingsNav } from '../../../../../ui'

export const Route = createFileRoute('/_auth/company/$companyId/settings/career-site')({
	component: RouteComponent,
})

function RouteComponent() {
	const { t } = useTranslation('common')
	const jobillaOrgId = useJobillaOrgId()
	const isJobillan = useIsJobillan()

	const relationQuery = useOrganisationRelationShow(jobillaOrgId, {
		enabled: !!jobillaOrgId,
	})

	const queryClient = useQueryClient()

	const organisationMutation = useOrganisationUpdate({
		onMutate: async (variables) => {
			await queryClient.cancelQueries({
				queryKey: organisationRelationShowQueryKey(jobillaOrgId),
			})

			const previousData: OrganisationRelationShowResponse | undefined = queryClient.getQueryData(
				organisationRelationShowQueryKey(jobillaOrgId),
			)

			if (previousData) {
				queryClient.setQueryData(organisationRelationShowQueryKey(jobillaOrgId), {
					...previousData,
					career_site_enabled: variables.data.careerSiteEnabled ?? previousData.careerSiteEnabled,
				})
			}

			return { previousData }
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: organisationRelationShowQueryKey(jobillaOrgId),
			})
		},
		onError: (_err, _data, context) => {
			const ctx = context as { previousData?: OrganisationRelationShowResponse } | undefined
			if (ctx?.previousData) {
				queryClient.setQueryData(organisationRelationShowQueryKey(jobillaOrgId), ctx.previousData)
			}
		},
	})

	const organisationSlug = relationQuery.data?.slug ?? ''
	const careerSiteEnabled = relationQuery.data?.careerSiteEnabled ?? false
	const formsBaseUrl = (import.meta.env.VITE_FORM_URL ?? 'https://forms.jobilla.com/').replace(/\/$/, '')
	const nexusApiUrl = (import.meta.env.VITE_NEXUS_API_URL ?? '').replace(/\/$/, '')
	const publicUrl = organisationSlug
		? `${formsBaseUrl}/careers/${organisationSlug}`
		: ''
	const rssFeedUrl = organisationSlug && nexusApiUrl
		? `${nexusApiUrl}/career-sites/${organisationSlug}/rss`
		: ''

	const viewModel: CareerSiteViewModel = {
		view: {
			careerSiteEnabled,
			canToggle: isJobillan,
			publicUrl,
			rssFeedUrl,
			organisationSlug,
		},
		loading: {
			organisation: relationQuery.isPending,
			saving: organisationMutation.isPending,
		},
		events: {
			onToggleCareerSite: (enabled) => {
				if (!jobillaOrgId) { return }

				organisationMutation.mutate({
					organisation: jobillaOrgId,
					data: { careerSiteEnabled: enabled },
				})
			},
		},
	}

	return (
		<SettingsNav active="career-site">
			<SettingsHeading>{t('careerSite.title')}</SettingsHeading>
			<CareerSiteSettings {...viewModel} />
		</SettingsNav>
	)
}
